import rehypeKatex from "rehype-katex"
import rehypeRaw from "rehype-raw"
import rehypeSanitize, { defaultSchema, type Options as SanitizeSchema } from "rehype-sanitize"
import remarkGfm from "remark-gfm"
import remarkMath from "remark-math"
import type { Options as MarkdownOptions } from "react-markdown"

import {
  CITATION_ANCHOR_ATTRIBUTES,
  RAW_TEXT_TAGS_STRIPPED_WITH_CONTENT,
  SECTIONING_TAGS_BEYOND_DEFAULT_SCHEMA,
  TEXT_FORMATTING_TAG_ATTRIBUTES,
  TEXT_FORMATTING_TAGS_BEYOND_DEFAULT_SCHEMA,
} from "./chatHtmlTags"
import { remarkChatMarkers } from "./remarkChatMarkers"
import { remarkEscapeUnknownHtml } from "./remarkEscapeUnknownHtml"
import { remarkSoftBreaksAsLineBreaks } from "./remarkSoftBreaksAsLineBreaks"

type RemarkPlugins = NonNullable<MarkdownOptions["remarkPlugins"]>
type RehypePlugins = NonNullable<MarkdownOptions["rehypePlugins"]>

const SPANNING_CELL_ATTRIBUTES = ["colSpan", "rowSpan"]
const MATH_CLASS_NAMES = ["math", "language-math", "math-inline", "math-display"]
const DISPLAY_MATH_ONLY = { singleDollarTextMath: false }

export const chatSanitizeSchema: SanitizeSchema = {
  ...defaultSchema,
  tagNames: [
    ...(defaultSchema.tagNames ?? []),
    ...TEXT_FORMATTING_TAGS_BEYOND_DEFAULT_SCHEMA,
    ...SECTIONING_TAGS_BEYOND_DEFAULT_SCHEMA,
  ],
  attributes: {
    ...(defaultSchema.attributes ?? {}),
    ...TEXT_FORMATTING_TAG_ATTRIBUTES,
    a: [...(defaultSchema.attributes?.a ?? []), ...CITATION_ANCHOR_ATTRIBUTES],
    code: [...(defaultSchema.attributes?.code ?? []), ["className", ...MATH_CLASS_NAMES]],
    div: [...(defaultSchema.attributes?.div ?? []), ["className", ...MATH_CLASS_NAMES]],
    td: SPANNING_CELL_ATTRIBUTES,
    th: SPANNING_CELL_ATTRIBUTES,
  },
  strip: [...(defaultSchema.strip ?? []), ...RAW_TEXT_TAGS_STRIPPED_WITH_CONTENT],
}

export const artifactBodyRemarkPlugins: RemarkPlugins = [remarkGfm, [remarkMath, DISPLAY_MATH_ONLY], remarkEscapeUnknownHtml]

export const chatRemarkPlugins: RemarkPlugins = [remarkGfm, [remarkMath, DISPLAY_MATH_ONLY], remarkChatMarkers, remarkEscapeUnknownHtml]

const USER_MESSAGE_MARKERS = { kinds: ["scroll-to-form"] as const }

export const userMessageRemarkPlugins: RemarkPlugins = [
  remarkGfm,
  [remarkMath, DISPLAY_MATH_ONLY],
  [remarkChatMarkers, USER_MESSAGE_MARKERS],
  remarkEscapeUnknownHtml,
  remarkSoftBreaksAsLineBreaks,
]

export const chatRehypePlugins: RehypePlugins = [rehypeRaw, [rehypeSanitize, chatSanitizeSchema], rehypeKatex]
